// Framing: how a photo fills a frame of any shape without cutting off its subject.
//
// Each wall photo can carry where its subject is (the photo agent computes it: face boxes
// when there are faces, otherwise a saliency estimate): `core` must stay in view (the
// faces), `loose` should if it can (faces plus head room and shoulders). The photo then:
//
// - fills the frame ("cover") when the loose box fits, or the core box fits (for faces, only
//   if not much more than half the photo would be cut away: a face cropped forehead to chin
//   reads badly); positioned so the subject sits where the eye expects it (faces a little
//   above the middle);
// - otherwise shrinks just enough to keep the subject ("fit"), over a soft blurred copy of
//   itself, so a tall group photo in a wide frame keeps everyone;
// - a panorama (or very tall photo) without faces in a very different frame slowly pans
//   across at full height ("pan") when motion is on, instead of shrinking to a strip, as
//   long as the sweep stays slow (at most PAN_TRAVEL frame lengths); otherwise it fits.
//
// Pure maths so it can be tested; the widgets turn the result into CSS.

export interface FracBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Framable {
  width: number;
  height: number;
  /** Where to centre, as fractions of the photo. */
  focus?: { x: number; y: number } | null;
  /** [x, y, w, h] fractions: `core` must stay in view, `loose` should. */
  subject?: { core: number[]; loose: number[]; faces?: number } | null;
}

export interface Framing {
  mode: "cover" | "fit" | "pan";
  /** The image's rectangle in frame pixels (the start position when panning). */
  left: number;
  top: number;
  width: number;
  height: number;
  /** True when the image leaves part of the frame bare (show the blurred backdrop). */
  backdrop: boolean;
  /** Ken Burns origin, as fractions of the image (the focus point). */
  origin: { x: number; y: number };
  /** The largest slow zoom that keeps the subject in view (1 = none). */
  zoom: number;
  /** Pan travel in pixels from the start position (pan mode only). */
  pan: { dx: number; dy: number } | null;
}

const NO_SUBJECT = { core: { x: 0.3, y: 0.25, w: 0.4, h: 0.45 }, loose: { x: 0.15, y: 0.12, w: 0.7, h: 0.76 } };
const MAX_ZOOM = 1.1;
const MIN_COVER_SHOWN = 0.5; // cover may hide up to half the photo when the faces still fit
const PAN_MISMATCH = 1.9; // photo vs frame shape at which a faceless panorama pans
const PANORAMA = 2; // a photo this many times longer than wide (either way) is a panorama
const PAN_TRAVEL = 3; // at most this many frame lengths of travel
const SNAP_TO_COVER = 0.94; // a "fit" this close to cover just covers

const box = (a: number[] | undefined, d: FracBox): FracBox =>
  a && a.length === 4 && a.every((v) => Number.isFinite(v)) && a[2]! > 0 && a[3]! > 0 ? { x: a[0]!, y: a[1]!, w: a[2]!, h: a[3]! } : d;

/** Offset along one axis: frame length L, displayed length D. */
function place(L: number, D: number, focus: number, anchor: number, boxes: [number, number][]): number {
  if (D <= L) return (L - D) / 2;
  let lo = L - D;
  let hi = 0;
  for (const [b0, b1] of boxes) {
    // Keep [b0, b1] (fractions of D) inside [0, L], if that's possible within what's allowed so far:
    // its left edge off + b0·D ≥ 0, its right edge off + b1·D ≤ L.
    const nlo = Math.max(lo, -b0 * D);
    const nhi = Math.min(hi, L - b1 * D);
    if (nlo <= nhi + 0.5) {
      lo = nlo;
      hi = Math.max(nlo, nhi);
    }
  }
  return Math.min(hi, Math.max(lo, L * anchor - focus * D));
}

/** Largest zoom about (ox, oy) that keeps [x0, x1] × [y0, y1] inside the frame. */
function zoomLimit(W: number, H: number, ox: number, oy: number, b: { x0: number; x1: number; y0: number; y1: number }): number {
  let k = MAX_ZOOM;
  if (b.x0 < ox) k = Math.min(k, ox / (ox - b.x0));
  if (b.x1 > ox) k = Math.min(k, (W - ox) / (b.x1 - ox));
  if (b.y0 < oy) k = Math.min(k, oy / (oy - b.y0));
  if (b.y1 > oy) k = Math.min(k, (H - oy) / (b.y1 - oy));
  return Math.max(1, k);
}

export function frameFor(photo: Framable, W: number, H: number, opts: { motion?: boolean } = {}): Framing {
  const w = photo.width;
  const h = photo.height;
  if (!(w > 0 && h > 0 && W > 0 && H > 0)) {
    return { mode: "cover", left: 0, top: 0, width: W, height: H, backdrop: false, origin: { x: 0.5, y: 0.5 }, zoom: 1, pan: null };
  }
  const known = !!photo.subject;
  const faces = (photo.subject?.faces ?? 0) > 0;
  const core = box(photo.subject?.core, NO_SUBJECT.core);
  const loose = box(photo.subject?.loose, NO_SUBJECT.loose);
  const focus = photo.focus && Number.isFinite(photo.focus.x) && Number.isFinite(photo.focus.y) ? photo.focus : { x: core.x + core.w / 2, y: core.y + core.h / 2 };

  const cover = Math.max(W / w, H / h);
  const contain = Math.min(W / w, H / h);
  const fits = (b: FracBox, s: number) => b.w * w * s <= W * 1.001 && b.h * h * s <= H * 1.001;
  const fitScale = (b: FracBox) => Math.min((W * 0.96) / (b.w * w), (H * 0.96) / (b.h * h));
  const mismatch = Math.max(w / h / (W / H), W / H / (w / h));

  let mode: Framing["mode"] = "cover";
  let s = cover;
  const panorama = Math.max(w / h, h / w) >= PANORAMA;
  const travel = w / h > W / H ? (w * cover - W) / W : (h * cover - H) / H;
  if (opts.motion && !faces && panorama && mismatch >= PAN_MISMATCH && travel <= PAN_TRAVEL) {
    mode = "pan"; // the whole panorama, a slow sweep at a time
  } else if (!fits(loose, cover) && !(fits(core, cover) && (!faces || contain / cover >= MIN_COVER_SHOWN))) {
    s = Math.min(cover, Math.max(contain, fitScale(loose)));
    if (s >= cover * SNAP_TO_COVER) s = cover;
    else mode = "fit";
  }
  // Without subject data a photo only shrinks for a big shape mismatch (old behaviour), never for a guessed box.
  if (!known && mode === "fit" && mismatch < 1.45) {
    mode = "cover";
    s = cover;
  }

  const D = { w: w * s, h: h * s };
  const anchorY = faces ? 0.42 : 0.5;
  const left = place(W, D.w, focus.x, 0.5, [
    [core.x, core.x + core.w],
    [loose.x, loose.x + loose.w],
  ]);
  const top = place(H, D.h, focus.y, anchorY, [
    [core.y, core.y + core.h],
    [loose.y, loose.y + loose.h],
  ]);

  let pan: Framing["pan"] = null;
  let start = { left, top };
  if (mode === "pan") {
    // Sweep the long side end to end; which way depends on where the subject leans.
    const towardEnd = focus.x >= 0.5;
    if (D.w > W) {
      const a = 0;
      const b = W - D.w;
      start = { left: towardEnd ? a : b, top };
      pan = { dx: towardEnd ? b - a : a - b, dy: 0 };
    } else {
      const a = 0;
      const b = H - D.h;
      start = { left, top: focus.y >= 0.5 ? a : b };
      pan = { dx: 0, dy: focus.y >= 0.5 ? b - a : a - b };
    }
  }

  // Slow zoom about the focus point, limited so the subject stays in the frame.
  const ox = left + focus.x * D.w;
  const oy = top + focus.y * D.h;
  const inFrame = (b: FracBox) => ({ x0: left + b.x * D.w, x1: left + (b.x + b.w) * D.w, y0: top + b.y * D.h, y1: top + (b.y + b.h) * D.h });
  const lb = inFrame(loose);
  const looseVisible = lb.x0 >= -1 && lb.y0 >= -1 && lb.x1 <= W + 1 && lb.y1 <= H + 1;
  const guard = inFrame(looseVisible ? loose : core);
  const clipped = { x0: Math.max(0, guard.x0), x1: Math.min(W, guard.x1), y0: Math.max(0, guard.y0), y1: Math.min(H, guard.y1) };
  const zoom = mode === "pan" ? 1 : Math.round(zoomLimit(W, H, ox, oy, clipped) * 1000) / 1000;

  return {
    mode,
    left: start.left,
    top: start.top,
    width: D.w,
    height: D.h,
    backdrop: D.w < W - 1 || D.h < H - 1,
    origin: { x: focus.x, y: focus.y },
    zoom,
    pan,
  };
}
